import { useState, useEffect, useContext } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Authentication layout components
import BasicLayoutLanding from "layouts/authentication/components/BasicLayoutLanding";

// Context
import { AuthContext, useMaterialUIController } from "context";

// Services
import AuthService from "services/auth-service";

// Images
import bgImage from "assets/images/bg-reset-cover.jpeg";

function ResetPassword() {
  const authContext = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [token, setToken] = useState(searchParams.get("token") || "");
  
  const [inputs, setInputs] = useState({
    password: "",
    password_confirmation: "",
  });

  const [notification, setNotification] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Garantir sincronia se a URL mudar sem desmontar o componente
  useEffect(() => {
    const urlEmail = searchParams.get("email");
    const urlToken = searchParams.get("token");

    if (urlEmail && urlEmail !== email) setEmail(urlEmail);
    if (urlToken && urlToken !== token) setToken(urlToken);
  }, [searchParams, email, token]);

  const changeHandler = (e) => {
    setInputs({
      ...inputs,
      [e.target.name]: e.target.value,
    });
  };

  // Tarefa 3: Envio para a API
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const submitToken = searchParams.get("token") || token;
    const submitEmail = email || searchParams.get("email");

    if (!submitToken) {
      setError("Token inválido ou ausente. Verifique o link de redefinição.");
      return;
    }

    if (!submitEmail) {
      setError("E-mail inválido ou ausente.");
      return;
    }

    if (inputs.password.length < 8) {
      setError("A senha deve ter no mínimo 8 caracteres.");
      return;
    }

    if (inputs.password !== inputs.password_confirmation) {
      setError("As senhas não conferem.");
      return;
    }

    const payload = {
      email: submitEmail,
      token: submitToken,
      password: inputs.password,
      password_confirmation: inputs.password_confirmation,
    };

    setLoading(true);

    try {
      await AuthService.resetPassword(payload);
      
      // Tentar login automático com a nova senha
      try {
        const loginPayload = {
          data: {
            type: "token",
            attributes: { email: submitEmail, password: inputs.password },
          },
        };
        const response = await AuthService.login(loginPayload);

        if (response.access_token) {
          localStorage.setItem("token", response.access_token);
          authContext.login(response.access_token, response.refresh_token);
          navigate("/dashboard", { replace: true });
        }
      } catch (loginError) {
        navigate("/auth/login", { replace: true });
      }

    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Erro ao redefinir senha. Tente novamente.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasicLayoutLanding coverHeight="50vh" image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor={sidenavColor}
          borderRadius="lg"
          coloredShadow={sidenavColor}
          mx={2}
          mt={-3}
          py={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h3" fontWeight="medium" color="white" mt={1}>
            Nova Senha
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            Digite sua nova senha abaixo
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <MDBox mb={2}>
              <MDInput type="password" label="Nova Senha" name="password" value={inputs.password} onChange={changeHandler} fullWidth />
            </MDBox>
            <MDBox mb={2}>
              <MDInput type="password" label="Confirmar Senha" name="password_confirmation" value={inputs.password_confirmation} onChange={changeHandler} fullWidth />
            </MDBox>
            
            {error && <MDTypography variant="caption" color="error" fontWeight="light" display="block" mb={2}>{error}</MDTypography>}

            <MDBox mt={4} mb={1}>
              <MDButton variant="gradient" color={sidenavColor} fullWidth type="submit" disabled={loading}>
                {loading ? "Alterando..." : "Alterar Senha"}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="caption" color="text">
                Ao continuar, você concorda com nossos{" "}
                <a href="https://djparafestas.com.br/termos-uso" target="_blank" style={{ color: "inherit", textDecoration: "underline" }}>
                  Termos de Uso
                </a>
                {" "}e{" "}
                <a href="https://djparafestas.com.br/politica-de-privacidade" target="_blank" style={{ color: "inherit", textDecoration: "underline" }}>
                  Política de Privacidade
                </a>.
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
      {notification && <MDAlert color="success" mt="20px"><MDTypography variant="body2" color="white">Senha alterada com sucesso! Redirecionando...</MDTypography></MDAlert>}
    </BasicLayoutLanding>
  );
}

export default ResetPassword;